import React from "react";
import { getImageUrl } from "utils/imageHelper";

export default function ViewArticleDetails({ data }) {
  if (!data) return null;

  const resolveMedia = (mediaPath) => {
    if (!mediaPath) return null;
    return typeof mediaPath === "string" ? getImageUrl(mediaPath) : null;
  };

  const isActive =
    data.status?.toLowerCase() === "active" || data.status === true;

  const formatDate = (isoStr) => {
    if (!isoStr) return "N/A";
    try {
      const date = new Date(isoStr);
      return date.toLocaleString();
    } catch {
      return isoStr;
    }
  };

  // Resolve default media path & type
  let defaultMediaUrl = null;
  let defaultMediaType = "image";

  if (data.default_media) {
    const meta =
      typeof data.default_media === "string"
        ? (() => {
            try {
              return JSON.parse(data.default_media);
            } catch {
              return { value: data.default_media, mediaType: "image" };
            }
          })()
        : data.default_media;

    defaultMediaUrl = resolveMedia(meta?.value || meta?.path || meta);
    defaultMediaType = meta?.mediaType || "image";
  }

  const galleryImages = Array.isArray(data.articleImages)
    ? data.articleImages.map(resolveMedia).filter(Boolean)
    : [];

  const galleryVideos = Array.isArray(data.articleVideos)
    ? data.articleVideos.map(resolveMedia).filter(Boolean)
    : [];

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* SECTION 1: ARTICLE DETAILS */}
      <ViewSection title="Article Details">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Title" value={data.title} />

          <ViewDisplayBox
            label="Exhibitor"
            value={data.exhibitor_id?.title || "N/A"}
          />

          <ViewDisplayBox label="Author" value={data.author} />

          <ViewDisplayBox
            label="Published Date"
            value={formatDate(data.published_date)}
          />

          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Description</span>
            <div
              className="view-value"
              dangerouslySetInnerHTML={{
                __html: data.description || "N/A",
              }}
            />
          </div>
        </div>
      </ViewSection>

      {/* SECTION 2: STATUS */}
      <ViewSection title="Status">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="view-label">Status</span>
            <div className="view-value flex items-center">
              <span>{isActive ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 3: ARTICLE MEDIA (PRODUCT VIEW PATTERN) */}
      <ViewSection title="Article Media">
        <div className="flex flex-col gap-4">
          {/* Featured / Default Media */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Default Media</span>
            <MediaContainer
              src={defaultMediaUrl}
              type={defaultMediaType}
              alt="Default Article Media"
              placeholder="No Default Media Set"
              height="h-36"
            />
          </div>

          {/* Gallery Images */}
          {galleryImages.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Images</span>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {galleryImages.map((url, idx) => (
                  <MediaContainer
                    key={idx}
                    src={url}
                    type="image"
                    alt={`Article Image ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-28"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Gallery Videos */}
          {galleryVideos.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Videos</span>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {galleryVideos.map((url, idx) => (
                  <MediaContainer
                    key={idx}
                    src={url}
                    type="video"
                    alt={`Article Video ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-36"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ViewSection>
    </div>
  );
}

/* =========================================================
   REUSABLE SECTION & BOX COMPONENTS
========================================================= */

function ViewSection({ title, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ViewDisplayBox({ label, value, className = "" }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="view-label">{label}</span>
      <div className="view-value">{value || "N/A"}</div>
    </div>
  );
}

/* =========================================================
   MEDIA CONTAINER (IMAGE & VIDEO HANDLER)
========================================================= */

function MediaContainer({
  src,
  type = "image",
  alt,
  placeholder,
  height = "h-28",
}) {
  return (
    <div className={`${height} view-image-div`}>
      {src ? (
        type === "video" ? (
          <video
            src={src}
            controls
            className="h-full w-full object-cover rounded-lg"
          />
        ) : (
          <img
            src={src}
            alt={alt || "Media preview"}
            className="max-h-full max-w-full object-contain"
          />
        )
      ) : (
        <span className="text-xs text-gray-400">{placeholder}</span>
      )}
    </div>
  );
}